import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  CheckCircle2,
  ClipboardList,
  Eye,
  EyeOff,
  FileUp,
  MessageSquare,
  Paperclip,
  ShieldAlert,
  UserCheck,
  UserPlus,
  XCircle,
  Activity,
} from "lucide-react";

import { pageTransition, formatDate } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import {
  addComment,
  addWatcher,
  assignComplaint,
  dismissComplaint,
  getComplaint,
  listComments,
  listEvents,
  listEvidence,
  listWatchers,
  removeWatcher,
  resolveComplaint,
  triage,
  uploadEvidence,
} from "@/api/complaints";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { Tabs } from "@/components/ui/Tabs";
import { SkeletonDetailPage } from "@/components/ui/Skeleton";
import { RoleGate } from "@/components/auth/RoleGate";
import {
  COMPLAINT_CATEGORY_LABELS,
  COMPLAINT_EVENT_LABELS,
  COMPLAINT_SEVERITY_LABELS,
  COMPLAINT_STATUS_LABELS,
  SEVERITY_BADGE_VARIANT,
  STATUS_BADGE_VARIANT,
  type Complaint,
  type ComplaintCategory,
  type ComplaintComment,
  type ComplaintEvent,
  type ComplaintEvidence,
  type ComplaintSeverity,
  type ComplaintWatcher,
} from "@/types/complaint";

export function ComplaintDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { hasRole } = usePermissions();
  const { user } = useAuth();
  const canInvestigate = hasRole("SUPER_ADMIN", "HR_ADMIN", "MANAGER");

  const [complaint, setComplaint] = useState<Complaint | null>(null);
  const [events, setEvents] = useState<ComplaintEvent[]>([]);
  const [comments, setComments] = useState<ComplaintComment[]>([]);
  const [evidence, setEvidence] = useState<ComplaintEvidence[]>([]);
  const [watchers, setWatchers] = useState<ComplaintWatcher[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("timeline");
  const [isBusy, setIsBusy] = useState(false);

  // Comment form
  const [commentBody, setCommentBody] = useState("");
  const [commentInternal, setCommentInternal] = useState(true);

  // Watcher form
  const [watcherUserId, setWatcherUserId] = useState("");

  // Evidence upload
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [evidenceSensitive, setEvidenceSensitive] = useState(false);

  // Modals
  const [showTriage, setShowTriage] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showResolve, setShowResolve] = useState(false);
  const [showDismiss, setShowDismiss] = useState(false);

  // Triage form
  const [triageSeverity, setTriageSeverity] = useState<ComplaintSeverity>("MEDIUM");
  const [triageCategory, setTriageCategory] = useState<ComplaintCategory>("OTHER");
  const [triageExpected, setTriageExpected] = useState("");
  const [triageAssignedTo, setTriageAssignedTo] = useState("");

  // Assign form
  const [assignUserId, setAssignUserId] = useState("");

  // Resolve form
  const [resolveOutcome, setResolveOutcome] = useState("");
  const [resolveNotes, setResolveNotes] = useState("");

  // Dismiss form
  const [dismissReason, setDismissReason] = useState("");

  const fetchAll = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const [c, ev, com, ev2, w] = await Promise.all([
        getComplaint(id),
        listEvents(id).catch(() => [] as ComplaintEvent[]),
        listComments(id).catch(() => [] as ComplaintComment[]),
        listEvidence(id).catch(() => [] as ComplaintEvidence[]),
        listWatchers(id).catch(() => [] as ComplaintWatcher[]),
      ]);
      setComplaint(c);
      setEvents(ev);
      setComments(com);
      setEvidence(ev2);
      setWatchers(w);
      setTriageSeverity(c.severity);
      setTriageCategory(c.category);
    } catch {
      addToast("error", "Failed to load complaint");
    } finally {
      setIsLoading(false);
    }
  }, [id, addToast]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleTriage = async () => {
    if (!complaint) return;
    setIsBusy(true);
    try {
      await triage(complaint.id, {
        severity: triageSeverity,
        category: triageCategory,
        expected_resolution_at: triageExpected || undefined,
        assigned_to: triageAssignedTo.trim() || undefined,
      });
      addToast("success", "Complaint triaged");
      setShowTriage(false);
      setTriageExpected("");
      setTriageAssignedTo("");
      await fetchAll();
    } catch {
      addToast("error", "Failed to triage");
    } finally {
      setIsBusy(false);
    }
  };

  const handleAssign = async () => {
    if (!complaint || !assignUserId.trim()) return;
    setIsBusy(true);
    try {
      await assignComplaint(complaint.id, assignUserId.trim());
      addToast("success", "Assigned");
      setShowAssign(false);
      setAssignUserId("");
      await fetchAll();
    } catch {
      addToast("error", "Failed to assign");
    } finally {
      setIsBusy(false);
    }
  };

  const handleResolve = async () => {
    if (!complaint || !resolveOutcome.trim()) return;
    setIsBusy(true);
    try {
      await resolveComplaint(complaint.id, {
        resolution_outcome: resolveOutcome.trim(),
        resolution_notes: resolveNotes.trim() || undefined,
      });
      addToast("success", "Complaint resolved");
      setShowResolve(false);
      setResolveOutcome("");
      setResolveNotes("");
      await fetchAll();
    } catch {
      addToast("error", "Failed to resolve");
    } finally {
      setIsBusy(false);
    }
  };

  const handleDismiss = async () => {
    if (!complaint || !dismissReason.trim()) return;
    setIsBusy(true);
    try {
      await dismissComplaint(complaint.id, { reason: dismissReason.trim() });
      addToast("success", "Complaint dismissed");
      setShowDismiss(false);
      setDismissReason("");
      await fetchAll();
    } catch {
      addToast("error", "Failed to dismiss");
    } finally {
      setIsBusy(false);
    }
  };

  const handleAddComment = async () => {
    if (!complaint || !commentBody.trim()) return;
    // Optimistic comment: render the investigator's note immediately with a
    // provisional id so the thread feels conversational. If the server
    // rejects we yank the placeholder and restore the draft so nothing is
    // silently lost. Temp id is tagged so we can tell it apart from a real
    // uuid if we ever need to key off it elsewhere.
    const body = commentBody.trim();
    const isInternal = commentInternal;
    const tempId = `temp-${Date.now()}`;
    const authorName =
      [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
      user?.email ||
      "You";
    const optimistic: ComplaintComment = {
      id: tempId,
      complaint_id: complaint.id,
      author_user_id: user?.id ?? null,
      author_name: authorName,
      body,
      is_internal: isInternal,
      created_at: new Date().toISOString(),
    };
    setComments((prev) => [...prev, optimistic]);
    setCommentBody("");
    setIsBusy(true);
    try {
      await addComment(complaint.id, body, isInternal);
      // Swap the placeholder for the server's copy and refresh the
      // timeline (which the server writes a COMMENT_ADDED event into).
      const [com, ev] = await Promise.all([
        listComments(complaint.id),
        listEvents(complaint.id),
      ]);
      setComments(com);
      setEvents(ev);
      addToast("success", "Comment added");
    } catch {
      setComments((prev) => prev.filter((c) => c.id !== tempId));
      setCommentBody(body);
      addToast("error", "Failed to add comment");
    } finally {
      setIsBusy(false);
    }
  };

  const handleAddWatcher = async () => {
    if (!complaint || !watcherUserId.trim()) return;
    setIsBusy(true);
    try {
      await addWatcher(complaint.id, watcherUserId.trim());
      addToast("success", "Watcher added");
      setWatcherUserId("");
      const w = await listWatchers(complaint.id);
      setWatchers(w);
    } catch {
      addToast("error", "Failed to add watcher");
    } finally {
      setIsBusy(false);
    }
  };

  const handleRemoveWatcher = async (userId: string) => {
    if (!complaint) return;
    try {
      await removeWatcher(complaint.id, userId);
      addToast("success", "Watcher removed");
      const w = await listWatchers(complaint.id);
      setWatchers(w);
    } catch {
      addToast("error", "Failed to remove watcher");
    }
  };

  const handleEvidenceFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!complaint || !file) return;
    setIsBusy(true);
    try {
      await uploadEvidence(complaint.id, file, evidenceSensitive);
      addToast("success", "Evidence uploaded");
      const ev = await listEvidence(complaint.id);
      setEvidence(ev);
      const events2 = await listEvents(complaint.id);
      setEvents(events2);
    } catch {
      addToast("error", "Failed to upload evidence");
    } finally {
      setIsBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (isLoading) {
    // Detail-shaped skeleton: header + main/side panels. Matches where
    // the complaint card, timeline, and watchers column actually land.
    return <SkeletonDetailPage />;
  }

  if (!complaint) {
    return (
      <div className="text-center py-16">
        <p className="text-text-muted">Complaint not found</p>
        <Button
          variant="ghost"
          onClick={() => navigate("/complaints")}
          className="mt-4"
        >
          Back to complaints
        </Button>
      </div>
    );
  }

  const tabs = [
    { id: "timeline", label: "Timeline", count: events.length },
    { id: "comments", label: "Comments", count: comments.length },
    { id: "evidence", label: "Evidence", count: evidence.length },
  ];

  return (
    <motion.div {...pageTransition}>
      <button
        onClick={() => navigate("/complaints")}
        className="inline-flex items-center gap-2 text-sm text-text-muted hover:text-text-primary mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to complaints
      </button>

      <PageHeader
        title={complaint.title}
        subtitle={
          complaint.is_anonymous
            ? "Anonymous report"
            : complaint.submitter_name || "Reporter unknown"
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={STATUS_BADGE_VARIANT[complaint.status]}>
              {COMPLAINT_STATUS_LABELS[complaint.status]}
            </Badge>
            <Badge variant={SEVERITY_BADGE_VARIANT[complaint.severity]}>
              {COMPLAINT_SEVERITY_LABELS[complaint.severity]}
            </Badge>
            <Badge variant="default">
              {COMPLAINT_CATEGORY_LABELS[complaint.category]}
            </Badge>
          </div>
        }
      />

      {/* Action row */}
      <RoleGate allowed={["SUPER_ADMIN", "HR_ADMIN", "MANAGER"]}>
        <div className="flex flex-wrap gap-2 mb-6">
          <Button variant="secondary" onClick={() => setShowTriage(true)}>
            <ClipboardList className="h-4 w-4" />
            Triage
          </Button>
          <Button variant="secondary" onClick={() => setShowAssign(true)}>
            <UserCheck className="h-4 w-4" />
            Assign
          </Button>
          <Button variant="secondary" onClick={() => setShowResolve(true)}>
            <CheckCircle2 className="h-4 w-4" />
            Resolve
          </Button>
          <Button variant="ghost" onClick={() => setShowDismiss(true)}>
            <XCircle className="h-4 w-4" />
            Dismiss
          </Button>
        </div>
      </RoleGate>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Description</CardTitle>
            </CardHeader>
            <p className="text-sm text-text-primary whitespace-pre-wrap">
              {complaint.description}
            </p>
          </Card>

          <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

          {activeTab === "timeline" && (
            <Card>
              {events.length === 0 ? (
                <p className="text-sm text-text-muted">No activity yet.</p>
              ) : (
                <ul className="space-y-3">
                  {events.map((e) => (
                    <li key={e.id} className="flex items-start gap-3">
                      <div className="rounded-full bg-accent/10 p-1.5 mt-0.5">
                        <Activity className="h-3.5 w-3.5 text-accent" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-text-primary">
                          {COMPLAINT_EVENT_LABELS[e.event_type] || e.event_type}
                        </p>
                        <p className="text-xs text-text-muted">
                          {formatDate(e.created_at, "MMM d, yyyy h:mm a")}
                          {!e.is_visible_to_reporter && (
                            <span className="ml-2 inline-flex items-center gap-1 text-text-muted">
                              <EyeOff className="h-3 w-3" /> internal
                            </span>
                          )}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}

          {activeTab === "comments" && (
            <Card>
              <div className="space-y-4">
                {comments.length === 0 ? (
                  <p className="text-sm text-text-muted">No comments yet.</p>
                ) : (
                  <ul className="space-y-3">
                    {comments.map((c) => (
                      <li
                        key={c.id}
                        className="rounded-lg border border-border p-3"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-text-primary">
                            {c.author_name || "—"}
                          </span>
                          <span className="text-xs text-text-muted inline-flex items-center gap-1">
                            {c.is_internal ? (
                              <>
                                <EyeOff className="h-3 w-3" /> internal
                              </>
                            ) : (
                              <>
                                <Eye className="h-3 w-3" /> visible to reporter
                              </>
                            )}
                            <span className="ml-2">
                              {formatDate(c.created_at, "MMM d, yyyy h:mm a")}
                            </span>
                          </span>
                        </div>
                        <p className="text-sm text-text-primary whitespace-pre-wrap">
                          {c.body}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="border-t border-border pt-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      Add comment
                    </label>
                    <textarea
                      value={commentBody}
                      onChange={(e) => setCommentBody(e.target.value)}
                      rows={3}
                      className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/50"
                      placeholder="Investigation notes, response to reporter…"
                    />
                  </div>
                  {canInvestigate && (
                    <label className="flex items-center gap-2 text-sm text-text-primary cursor-pointer">
                      <input
                        type="checkbox"
                        checked={commentInternal}
                        onChange={(e) => setCommentInternal(e.target.checked)}
                        className="rounded border-border text-accent focus:ring-accent"
                      />
                      Internal only (hidden from reporter)
                    </label>
                  )}
                  <div className="flex justify-end">
                    <Button
                      onClick={handleAddComment}
                      isLoading={isBusy}
                      disabled={!commentBody.trim()}
                    >
                      <MessageSquare className="h-4 w-4" />
                      Add comment
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {activeTab === "evidence" && (
            <Card>
              <div className="space-y-4">
                {evidence.length === 0 ? (
                  <p className="text-sm text-text-muted">
                    No evidence uploaded.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {evidence.map((e) => (
                      <li
                        key={e.id}
                        className="flex items-center justify-between rounded-lg border border-border px-3 py-2"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Paperclip className="h-4 w-4 text-text-muted shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm text-text-primary truncate">
                              {e.filename}
                            </p>
                            <p className="text-xs text-text-muted">
                              {formatDate(e.created_at)}
                              {e.uploader_name ? ` • ${e.uploader_name}` : ""}
                            </p>
                          </div>
                        </div>
                        {e.is_sensitive && (
                          <Badge variant="danger">
                            <ShieldAlert className="h-3 w-3 mr-1" />
                            Sensitive
                          </Badge>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                <RoleGate allowed={["SUPER_ADMIN", "HR_ADMIN", "MANAGER"]}>
                  <div className="border-t border-border pt-4 space-y-3">
                    <label className="flex items-center gap-2 text-sm text-text-primary cursor-pointer">
                      <input
                        type="checkbox"
                        checked={evidenceSensitive}
                        onChange={(e) =>
                          setEvidenceSensitive(e.target.checked)
                        }
                        className="rounded border-border text-accent focus:ring-accent"
                      />
                      Mark as sensitive
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleEvidenceFile}
                      className="hidden"
                    />
                    <Button
                      variant="secondary"
                      onClick={() => fileInputRef.current?.click()}
                      isLoading={isBusy}
                    >
                      <FileUp className="h-4 w-4" />
                      Upload file
                    </Button>
                  </div>
                </RoleGate>
              </div>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-xs text-text-muted">Submitted</dt>
                <dd className="text-text-primary">
                  {formatDate(complaint.created_at)}
                </dd>
              </div>
              {complaint.assignee_name && (
                <div>
                  <dt className="text-xs text-text-muted">Assigned to</dt>
                  <dd className="text-text-primary">
                    {complaint.assignee_name}
                  </dd>
                </div>
              )}
              {complaint.triaged_at && (
                <div>
                  <dt className="text-xs text-text-muted">Triaged</dt>
                  <dd className="text-text-primary">
                    {formatDate(complaint.triaged_at)}
                  </dd>
                </div>
              )}
              {complaint.expected_resolution_at && (
                <div>
                  <dt className="text-xs text-text-muted">
                    Expected resolution
                  </dt>
                  <dd className="text-text-primary">
                    {formatDate(complaint.expected_resolution_at)}
                  </dd>
                </div>
              )}
              {complaint.resolved_at && (
                <div>
                  <dt className="text-xs text-text-muted">Resolved</dt>
                  <dd className="text-text-primary">
                    {formatDate(complaint.resolved_at)}
                  </dd>
                </div>
              )}
              {complaint.resolution_outcome && (
                <div>
                  <dt className="text-xs text-text-muted">Outcome</dt>
                  <dd className="text-text-primary whitespace-pre-wrap">
                    {complaint.resolution_outcome}
                  </dd>
                </div>
              )}
              {complaint.dismissed_at && (
                <div>
                  <dt className="text-xs text-text-muted">Dismissed</dt>
                  <dd className="text-text-primary">
                    {formatDate(complaint.dismissed_at)}
                  </dd>
                </div>
              )}
              {complaint.dismissed_reason && (
                <div>
                  <dt className="text-xs text-text-muted">Dismissed reason</dt>
                  <dd className="text-text-primary whitespace-pre-wrap">
                    {complaint.dismissed_reason}
                  </dd>
                </div>
              )}
            </dl>
          </Card>

          <RoleGate allowed={["SUPER_ADMIN", "HR_ADMIN", "MANAGER"]}>
            <Card>
              <CardHeader>
                <CardTitle>Watchers</CardTitle>
              </CardHeader>
              <ul className="space-y-2 mb-3">
                {watchers.length === 0 ? (
                  <li className="text-sm text-text-muted">No watchers.</li>
                ) : (
                  watchers.map((w) => (
                    <li
                      key={w.user_id}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="text-text-primary">
                        {w.user_name || w.user_id}
                      </span>
                      <button
                        onClick={() => handleRemoveWatcher(w.user_id)}
                        className="text-xs text-text-muted hover:text-danger"
                      >
                        remove
                      </button>
                    </li>
                  ))
                )}
              </ul>
              <div className="flex gap-2">
                <Input
                  value={watcherUserId}
                  onChange={(e) => setWatcherUserId(e.target.value)}
                  placeholder="User UUID"
                  className="flex-1"
                />
                <Button
                  size="sm"
                  onClick={handleAddWatcher}
                  isLoading={isBusy}
                  disabled={!watcherUserId.trim()}
                >
                  <UserPlus className="h-4 w-4" />
                  Add
                </Button>
              </div>
            </Card>
          </RoleGate>
        </div>
      </div>

      {/* Triage modal */}
      <Modal
        isOpen={showTriage}
        onClose={() => setShowTriage(false)}
        title="Triage complaint"
        size="md"
      >
        <div className="space-y-4">
          <Select
            label="Severity"
            value={triageSeverity}
            onChange={(e) =>
              setTriageSeverity(e.target.value as ComplaintSeverity)
            }
            options={Object.entries(COMPLAINT_SEVERITY_LABELS).map(
              ([v, l]) => ({ value: v, label: l }),
            )}
          />
          <Select
            label="Category"
            value={triageCategory}
            onChange={(e) =>
              setTriageCategory(e.target.value as ComplaintCategory)
            }
            options={Object.entries(COMPLAINT_CATEGORY_LABELS).map(
              ([v, l]) => ({ value: v, label: l }),
            )}
          />
          <Input
            label="Expected resolution (optional)"
            type="date"
            value={triageExpected}
            onChange={(e) => setTriageExpected(e.target.value)}
          />
          <Input
            label="Assign to (user UUID, optional)"
            value={triageAssignedTo}
            onChange={(e) => setTriageAssignedTo(e.target.value)}
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setShowTriage(false)}>
              Cancel
            </Button>
            <Button onClick={handleTriage} isLoading={isBusy}>
              Save triage
            </Button>
          </div>
        </div>
      </Modal>

      {/* Assign modal */}
      <Modal
        isOpen={showAssign}
        onClose={() => setShowAssign(false)}
        title="Assign complaint"
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="User UUID"
            value={assignUserId}
            onChange={(e) => setAssignUserId(e.target.value)}
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setShowAssign(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAssign}
              isLoading={isBusy}
              disabled={!assignUserId.trim()}
            >
              Assign
            </Button>
          </div>
        </div>
      </Modal>

      {/* Resolve modal */}
      <Modal
        isOpen={showResolve}
        onClose={() => setShowResolve(false)}
        title="Resolve complaint"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Resolution outcome
            </label>
            <textarea
              value={resolveOutcome}
              onChange={(e) => setResolveOutcome(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/50"
              placeholder="What was the outcome?"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Notes (optional)
            </label>
            <textarea
              value={resolveNotes}
              onChange={(e) => setResolveNotes(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/50"
              placeholder="Investigation findings…"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setShowResolve(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleResolve}
              isLoading={isBusy}
              disabled={!resolveOutcome.trim()}
            >
              Resolve
            </Button>
          </div>
        </div>
      </Modal>

      {/* Dismiss modal */}
      <Modal
        isOpen={showDismiss}
        onClose={() => setShowDismiss(false)}
        title="Dismiss complaint"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Reason
            </label>
            <textarea
              value={dismissReason}
              onChange={(e) => setDismissReason(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/50"
              placeholder="Why is this complaint being dismissed?"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setShowDismiss(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDismiss}
              isLoading={isBusy}
              disabled={!dismissReason.trim()}
            >
              Dismiss
            </Button>
          </div>
        </div>
      </Modal>
    </motion.div>
  );
}
